import { formatSupplementalNameEditPayload } from "@/services/helpers";
import { getSupplementalsByEntity } from "@/services/supplementals";
import type { DialogTabSaveContext } from "@/v2/framework/dialog/types";

export const MARKET_SUPPLEMENTAL_EDITS_KEY = "_marketSupplementalEdits";

export type MarketSupplementalEditRow = {
  supplementalNameTypeId: string | number;
  computedName: string | null;
  computedEventMarketName: string | null;
  parentName?: string | null;
  parentEventMarketName?: string | null;
  namePolicy?: number;
  eventMarketNamePolicy?: number;
};

export function buildAddMarketSupplementalNames(
  values: Record<string, unknown>,
): Record<string, unknown>[] {
  return getSupplementalsByEntity("MARKET")
    .map((type) => {
      const name = values[`supplemental_${type.id}`];
      const eventMarketName = values[`supplementalEvent_${type.id}`];
      return {
        supplementalNameTypeId: type.id,
        name: name == null || name === "" ? null : String(name),
        eventMarketName:
          eventMarketName == null || eventMarketName === "" ? null : String(eventMarketName),
      };
    })
    .filter((item) => item.name || item.eventMarketName);
}

export function buildMarketSupplementalNamesPatch(
  values: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> | null {
  const edits = values[MARKET_SUPPLEMENTAL_EDITS_KEY];
  if (!edits || typeof edits !== "object" || Array.isArray(edits)) return null;

  const editRows = Object.values(edits as Record<string, MarketSupplementalEditRow>);
  if (!editRows.length) return null;

  const baselineEdits = baseline[MARKET_SUPPLEMENTAL_EDITS_KEY];
  const baselineMap =
    baselineEdits && typeof baselineEdits === "object" && !Array.isArray(baselineEdits)
      ? (baselineEdits as Record<string, MarketSupplementalEditRow>)
      : {};

  let supplementalNames: Record<string, unknown>[] | null = null;
  const supplementalPolicies: Record<string, { name?: number; eventMarketName?: number }> = {};
  let hasChanges = false;

  for (const edit of editRows) {
    const key = String(edit.supplementalNameTypeId);
    const baselineRow = baselineMap[key];

    if ((baselineRow?.computedName ?? null) !== (edit.computedName ?? null)) {
      supplementalNames = formatSupplementalNameEditPayload({
        supplementalNameList: supplementalNames,
        supplementalNameTypeId: edit.supplementalNameTypeId,
        value: edit.computedName,
        key: "name",
      }) as Record<string, unknown>[];
      hasChanges = true;
    }

    if ((baselineRow?.computedEventMarketName ?? null) !== (edit.computedEventMarketName ?? null)) {
      supplementalNames = formatSupplementalNameEditPayload({
        supplementalNameList: supplementalNames,
        supplementalNameTypeId: edit.supplementalNameTypeId,
        value: edit.computedEventMarketName,
        key: "eventMarketName",
      }) as Record<string, unknown>[];
      hasChanges = true;
    }

    if ((baselineRow?.namePolicy ?? 1) !== (edit.namePolicy ?? 1)) {
      supplementalPolicies[key] = { ...supplementalPolicies[key], name: edit.namePolicy ?? 1 };
      hasChanges = true;
    }

    if ((baselineRow?.eventMarketNamePolicy ?? 1) !== (edit.eventMarketNamePolicy ?? 1)) {
      supplementalPolicies[key] = {
        ...supplementalPolicies[key],
        eventMarketName: edit.eventMarketNamePolicy ?? 1,
      };
      hasChanges = true;
    }
  }

  if (!hasChanges) return null;

  const patch: Record<string, unknown> = {};
  if (supplementalNames) patch.supplementalNames = supplementalNames;
  if (Object.keys(supplementalPolicies).length) {
    patch.sourcePolicyMap = { supplementalNames: supplementalPolicies };
  }
  return patch;
}

export function syncMarketSupplementalBaseline(ctx: DialogTabSaveContext): void {
  const edits = ctx.values[MARKET_SUPPLEMENTAL_EDITS_KEY];
  if (edits && typeof edits === "object" && !Array.isArray(edits)) {
    ctx.baseline[MARKET_SUPPLEMENTAL_EDITS_KEY] = JSON.parse(JSON.stringify(edits));
  }
}

export function syncMarketSpecifiersBaseline(ctx: DialogTabSaveContext): void {
  ctx.baseline.specifiers = JSON.parse(JSON.stringify(ctx.values.specifiers ?? []));
  ctx.baseline.competitors = JSON.parse(JSON.stringify(ctx.values.competitors ?? []));
}

function csv(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  return value == null ? "" : String(value);
}

function listFromCsv(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  if (value == null || value === "") return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function buildSpecifiersPatch(
  values: Record<string, unknown>,
  baseline: Record<string, unknown>,
): Record<string, unknown> | null {
  const current = JSON.stringify(values.specifiers ?? []);
  const previous = JSON.stringify(baseline.specifiers ?? []);
  const currentCompetitors = JSON.stringify(values.competitors ?? []);
  const previousCompetitors = JSON.stringify(baseline.competitors ?? []);

  const patch: Record<string, unknown> = {};
  if (current !== previous) {
    const specifiers = Array.isArray(values.specifiers) ? values.specifiers : [];
    patch.specifiers = specifiers.map((row) => {
      const specifier = row as Record<string, unknown>;
      return {
        ...specifier,
        defaultValues: listFromCsv(specifier.defaultValues),
        validations: listFromCsv(specifier.validations),
      };
    });
  }
  if (currentCompetitors !== previousCompetitors) {
    patch.competitors = values.competitors ?? [];
  }
  return Object.keys(patch).length ? patch : null;
}

export function displaySpecifierList(value: unknown): string {
  return csv(value);
}
